import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  convertLeadToPatient as convertLeadToPatientApi,
  postLeadBridge as postLeadBridgeApi,
  type ConvertLeadToPatientResultView,
  type LeadBridgeRequest,
  type LeadBridgeResultView,
  type LeadPersonChoiceRequest,
  type PatientConvertCandidateView,
} from '@/dashboard/api';
import { PortalApiError } from '@/lib/portal-api';
import {
  CONSENT_CHANNEL_OPTIONS,
  GENDER_OPTIONS,
  MINOR_AGE_YEARS,
  RELATIONSHIP_OPTIONS,
} from '@/dashboard/patients/patient-master-view';

/**
 * AGENT-TO-PATIENT-SPINE-V1 (V3-P4) + PATIENT-MASTER-CONVERSION-AND-PAYMENT-
 * LINK-COHERENCE-V1 -- the "Patient golden record" card: the convert moment
 * PLUS the guided create/link bridge behind its honest no-match outcome.
 *
 * LINK-FIRST stays the law: Convert dedupes the lead's phone fingerprint and
 * stamps leads.patient_id via the ONE conversion hook. When NO golden record
 * matches, the card no longer dead-ends -- a permitted operator opens ONE
 * guided flow ("Create patient from this lead"): the server re-runs the
 * match, LINKS on any hit, and only on a confirmed no-match CREATES via the
 * EXISTING registration writer with a SERVER-SIDE field copy, then links.
 *
 * PHI LAW: no phone is ever rendered or sent here. The preview shows the
 * copy MASKED ("Phone: hidden -- copied securely server-side"); the bridge
 * request carries only leadId + typed demographics (gender / age, guardian +
 * consent for a minor, a typed name ONLY when the lead has none).
 *
 * Backend auth is the security boundary (booking audience for convert;
 * requirePatientMasterCreateScope + the PATIENT_MASTER flag gate for the
 * bridge); the `bridgeEnabled` prop is UX gating only.
 */

export const CONVERT_BUTTON_COPY = 'Check who this number belongs to';
export const CONVERT_BUSY_COPY = 'Checking...';
export const CONVERT_SECTION_TITLE = 'Patient golden record';
export const NO_PATIENT_RECORD_COPY =
  'No matching patient record yet. Create the patient from this lead below - one step, no retyping.';
export const NO_PATIENT_RECORD_NO_BRIDGE_COPY =
  'No matching patient record. A front-desk operator with Patients access can create the patient from this lead.';
/** The confirmation prompt. Says WHY a choice is needed in front-desk words:
 *  one number is often a whole family, so the person has to be named. */
export const PERSON_CONFIRMATION_COPY =
  'This phone number is already on file. Families often share one number, so please confirm who this enquiry is about before linking.';
/**
 * The escape hatch, in TWO forms -- because the same answer has two different
 * consequences depending on which door asked, and a label must describe the
 * consequence it actually has.
 *
 * On the convert door the answer only leaves the lead alone. On the bridge
 * door ("Create patient from this lead") the SAME answer is what proceeds to
 * REGISTER a new patient record -- an irreversible, PHI-bearing write. One
 * shared caption across both would mean an operator pressing what reads as a
 * clarification and getting a new golden record.
 */
export const DIFFERENT_PERSON_BUTTON_COPY =
  'This enquiry is for a different person';
export const DIFFERENT_PERSON_CREATES_BUTTON_COPY =
  'This enquiry is for a different person - register them as a new patient';
/** The lead stays valid and unresolved -- work continues, nothing attached. */
export const DIFFERENT_PERSON_CONFIRMED_COPY =
  'Left unlinked - this enquiry is for a different person. The lead stays open and keeps its source and campaign details; you can link a patient later.';
/** Prefix of the per-person action label; the person's name completes it. */
export const LINK_TO_PERSON_PREFIX = 'Link this lead to';
export const BRIDGE_BUTTON_COPY = 'Create patient from this lead';
export const BRIDGE_BUSY_COPY = 'Creating and linking...';
export const BRIDGE_PREVIEW_PHONE_COPY =
  'Phone: hidden - copied securely server-side from the lead record.';
export const BRIDGE_PHONE_UNAVAILABLE_COPY =
  'This lead has no securely stored phone value to copy. Register the patient on the Patients page (reveal the phone from the lead first), then press Convert to link.';
export const BRIDGE_NO_PHONE_COPY =
  'This lead has no phone on record, so it cannot be matched or linked to a patient.';
export const BRIDGE_CREATED_NOT_LINKED_COPY =
  'The patient record was created but the link did not save. Press "Link now" to finish.';
export const RECORD_PAYMENT_DOOR_COPY = 'Record payment';
export const IDLE_NONE_COPY =
  'No patient linked yet. Convert checks for an existing patient by phone; if none exists you can create one from this lead in one step.';
export const IDLE_POSSIBLE_MATCH_COPY =
  'Possible existing patient - this phone matches a patient record. Press Convert to review and link.';

/** The on-load link state (record.goldenRecordLink mirror; absent = unknown). */
export interface InitialPatientLinkView {
  readonly status: 'linked' | 'possible_match' | 'none';
  readonly recordId?: string;
  readonly umr?: string;
  readonly candidateCount?: number;
}

export type ConvertState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'result'; result: ConvertLeadToPatientResultView }
  | { kind: 'bridge-form' }
  | { kind: 'bridge-busy' }
  | { kind: 'bridge-result'; result: LeadBridgeResultView }
  | { kind: 'error'; message: string };

function candidateLabel(c: PatientConvertCandidateView): string {
  const city = c.city !== null && c.city.trim().length > 0 ? ` - ${c.city}` : '';
  return `${c.fullName} (${c.umr})${city}`;
}

/** Format a stored date as a plain day the desk can read aloud. Invalid or
 *  absent input yields null so the field is OMITTED, never guessed at. */
function formatVisitDay(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** The identifying details under a person's name, as labelled fields rather
 *  than a comma-spliced sentence (FIELDS, NOT SENTENCES). Only facts the
 *  record actually carries are rendered -- an unknown age or an unvisited
 *  patient simply has no chip. */
function personDetails(
  c: PatientConvertCandidateView,
): ReadonlyArray<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  out.push({ label: 'UMR', value: c.umr });
  if (typeof c.ageYears === 'number' && Number.isFinite(c.ageYears)) {
    out.push({ label: 'Age', value: `${c.ageYears}` });
  }
  if (typeof c.gender === 'string' && c.gender.trim() !== '') {
    out.push({ label: 'Sex', value: c.gender });
  }
  if (c.city !== null && c.city !== undefined && c.city.trim() !== '') {
    out.push({ label: 'City', value: c.city });
  }
  const lastVisit = formatVisitDay(c.lastVisitAt);
  if (lastVisit !== null) out.push({ label: 'Last visit', value: lastVisit });
  return out;
}

function messageFor(err: unknown): string {
  if (err instanceof PortalApiError) {
    if (err.status === 401 || err.status === 403) {
      return 'You do not have permission to convert this lead.';
    }
    if (err.status === 409) {
      return 'This lead was just linked to a different patient. Reload.';
    }
    if (err.status === 503) {
      return 'Patient records are not enabled yet. Ask the owner to enable Patient Master.';
    }
    if (err.message.trim().length > 0) return err.message;
  }
  if (err instanceof Error && err.message.trim().length > 0) return err.message;
  return 'Could not convert the lead. Please try again.';
}

// --- Bridge form (typed demographics only; never a phone) -------------------

export interface BridgeFormState {
  readonly fullName: string;
  readonly gender: string;
  readonly ageYears: string;
  readonly guardianName: string;
  readonly guardianRelationship: string;
  readonly consentGrantedBy: string;
  readonly consentChannel: string;
}

export const EMPTY_BRIDGE_FORM: BridgeFormState = {
  fullName: '',
  gender: '',
  ageYears: '',
  guardianName: '',
  guardianRelationship: 'guardian',
  consentGrantedBy: '',
  consentChannel: 'in_person',
};

/** Age string -> int or null (mirror of the create form's tolerance). */
function parsedAge(raw: string): number | null {
  const n = Number(raw.trim());
  return raw.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 150 ? n : null;
}

/** Validate the bridge form; returns a plain-language error or null. */
export function validateBridgeForm(
  form: BridgeFormState,
  leadHasName: boolean,
): string | null {
  if (!leadHasName && form.fullName.trim() === '') {
    return 'Type the patient name - this lead was captured without one.';
  }
  if (form.gender.trim() === '') return 'Pick a gender.';
  const age = parsedAge(form.ageYears);
  if (age === null) return 'Enter the age in years (0-150).';
  if (age < MINOR_AGE_YEARS) {
    if (form.guardianName.trim() === '') {
      return 'A minor needs a guardian name before saving.';
    }
    if (form.consentGrantedBy.trim() === '') {
      return 'A minor needs recorded consent (who granted it).';
    }
  }
  return null;
}

/** Build the bridge request from the validated form. NEVER carries a phone. */
export function buildBridgeRequest(
  leadId: number,
  form: BridgeFormState,
  leadHasName: boolean,
): LeadBridgeRequest {
  const age = parsedAge(form.ageYears);
  const minor = age !== null && age < MINOR_AGE_YEARS;
  return {
    leadId,
    ...(leadHasName ? {} : { fullName: form.fullName.trim() }),
    gender: form.gender,
    ...(age !== null ? { ageYears: age } : {}),
    ...(minor
      ? {
          guardian: {
            name: form.guardianName.trim(),
            relationship: form.guardianRelationship,
          },
          consent: {
            grantedBy: form.consentGrantedBy.trim(),
            channel: form.consentChannel,
          },
        }
      : {}),
  };
}

// --- Pure view ---------------------------------------------------------------

export interface ConvertLeadToPatientViewProps {
  state: ConvertState;
  /** UX gate only (me.patientMasterEnabled + a create-audience role);
   *  the backend fence is the boundary. */
  bridgeEnabled: boolean;
  /** The lead's display name (drives the masked preview + the name field). */
  leadName: string | null;
  /** On-load link state from the record read (four honest states; absent =
   *  unknown -> the action-driven card, claiming nothing). */
  initialLink?: InitialPatientLinkView | null;
  /** Billing door builder (null = the viewer has no billing-entry access or
   *  the parent cannot build a door). The URL params ARE the prefill carrier
   *  -- the door survives any create/link round trip by re-supplying them. */
  paymentHrefFor?: ((patientId: string) => string | null) | null;
  form: BridgeFormState;
  onFormChange: (form: BridgeFormState) => void;
  onConvert: () => void;
  onPickCandidate: (patientId: string) => void;
  /** The operator states the enquiry is for somebody else: nothing attaches,
   *  the lead stays open. */
  onDifferentPerson: () => void;
  onOpenBridge: () => void;
  onSubmitBridge: () => void;
  onCancelBridge: () => void;
  /** Retry the link when a create landed but the stamp failed. */
  onRetryLink: () => void;
  formError: string | null;
}

function LinkedLine({
  patient,
  justCreated,
}: {
  patient: PatientConvertCandidateView | undefined;
  justCreated: boolean;
}): JSX.Element {
  return (
    <p
      className="lead360-muted"
      data-testid={justCreated ? 'convert-created-linked' : 'convert-linked'}
      data-tone="success"
    >
      {justCreated ? 'Patient created and linked: ' : 'Linked to patient '}
      {patient ? (
        <strong data-testid="convert-linked-patient">
          {candidateLabel(patient)}
        </strong>
      ) : (
        'record'
      )}
      .
    </p>
  );
}

/** The billing door for a linked patient (payment continuity: the URL
 *  params carry the identity + visit link back into billing entry). */
function PaymentDoor({
  patientId,
  paymentHrefFor,
}: {
  patientId: string | undefined;
  paymentHrefFor: ((patientId: string) => string | null) | null | undefined;
}): JSX.Element | null {
  if (patientId === undefined || !paymentHrefFor) return null;
  const href = paymentHrefFor(patientId);
  if (href === null) return null;
  return (
    <Link
      to={href}
      className="ac-btn ac-btn--secondary ac-btn--sm"
      data-testid="convert-record-payment"
    >
      {RECORD_PAYMENT_DOOR_COPY}
    </Link>
  );
}

/**
 * THE PERSON CONFIRMATION STEP. Every patient this number could belong to is
 * listed with the details that tell family members apart, and NONE is
 * preselected or ranked -- the operator names the person, or says the enquiry
 * is for somebody else. Each action label states its consequence, so nothing
 * is attached by a press whose effect was unclear.
 *
 * This renders for ONE possible patient exactly as for five: a single match
 * is a question, never an answer.
 */
function PersonConfirmation({
  candidates,
  onPick,
  onDifferentPerson,
  differentPersonCreates = false,
}: {
  candidates: readonly PatientConvertCandidateView[];
  onPick: (patientId: string) => void;
  onDifferentPerson: () => void;
  /** TRUE on the bridge door, where "different person" goes on to REGISTER a
   *  new patient -- the label must say so. */
  differentPersonCreates?: boolean;
}): JSX.Element {
  return (
    <div data-testid="convert-candidates">
      <p className="lead360-muted">{PERSON_CONFIRMATION_COPY}</p>
      <ul className="lead360-convert__candidates">
        {candidates.map((c) => (
          <li key={c.patientId} className="lead360-convert__person">
            <p className="lead360-convert__person-name">
              <strong>{c.fullName}</strong>
            </p>
            <dl className="lead360-convert__person-facts">
              {personDetails(c).map((f) => (
                <div key={f.label} className="lead360-convert__person-fact">
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
            <button
              type="button"
              className="ac-btn ac-btn--secondary ac-btn--sm"
              data-testid={`convert-candidate-${c.patientId}`}
              onClick={() => onPick(c.patientId)}
            >
              {`${LINK_TO_PERSON_PREFIX} ${c.fullName}`}
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="ac-btn ac-btn--ghost ac-btn--sm"
        data-testid="convert-different-person"
        onClick={onDifferentPerson}
      >
        {differentPersonCreates
          ? DIFFERENT_PERSON_CREATES_BUTTON_COPY
          : DIFFERENT_PERSON_BUTTON_COPY}
      </button>
    </div>
  );
}

/** Pure, SSR-testable view. */
export function ConvertLeadToPatientView({
  state,
  bridgeEnabled,
  leadName,
  initialLink = null,
  paymentHrefFor = null,
  form,
  onFormChange,
  onConvert,
  onPickCandidate,
  onDifferentPerson,
  onOpenBridge,
  onSubmitBridge,
  onCancelBridge,
  onRetryLink,
  formError,
}: ConvertLeadToPatientViewProps): JSX.Element {
  const leadHasName = leadName !== null && leadName.trim() !== '';
  const busy = state.kind === 'busy' || state.kind === 'bridge-busy';

  const linkedFromConvert =
    state.kind === 'result' && state.result.outcome === 'linked'
      ? state.result.linkedPatient
      : undefined;
  const bridgeResult = state.kind === 'bridge-result' ? state.result : null;
  // On-load 'linked' renders the linked card with NO action press needed.
  const idleLinked =
    state.kind === 'idle' && initialLink !== null && initialLink.status === 'linked';
  const isLinkedState =
    idleLinked ||
    (state.kind === 'result' && state.result.outcome === 'linked') ||
    (bridgeResult !== null &&
      (bridgeResult.outcome === 'linked' ||
        bridgeResult.outcome === 'created_and_linked'));

  return (
    <section
      className="lead360-section lead360-convert"
      aria-label={CONVERT_SECTION_TITLE}
      data-testid="convert-to-patient"
    >
      <h4 className="lead360-section__title">{CONVERT_SECTION_TITLE}</h4>

      {idleLinked ? (
        <div data-testid="convert-initial-linked">
          <p className="lead360-muted" data-tone="success">
            Linked to patient{' '}
            {initialLink?.umr !== undefined ? (
              <strong data-testid="convert-linked-patient">
                {initialLink.umr}
              </strong>
            ) : (
              'record'
            )}
            .
          </p>
          <PaymentDoor
            patientId={initialLink?.recordId}
            paymentHrefFor={paymentHrefFor}
          />
        </div>
      ) : state.kind === 'result' && state.result.outcome === 'linked' ? (
        <>
          <LinkedLine patient={linkedFromConvert} justCreated={false} />
          <PaymentDoor
            patientId={state.result.patientId}
            paymentHrefFor={paymentHrefFor}
          />
        </>
      ) : state.kind === 'result' && state.result.outcome === 'already_linked' ? (
        <p className="lead360-muted" data-testid="convert-already-linked">
          This lead is already linked to a patient record.
        </p>
      ) : state.kind === 'result' &&
        state.result.outcome === 'no_patient_record' ? (
        <div data-testid="convert-no-record">
          <p className="lead360-muted">
            {bridgeEnabled ? NO_PATIENT_RECORD_COPY : NO_PATIENT_RECORD_NO_BRIDGE_COPY}
          </p>
          {bridgeEnabled ? (
            <button
              type="button"
              className="ac-btn ac-btn--primary ac-btn--sm"
              data-testid="bridge-open"
              onClick={onOpenBridge}
            >
              {BRIDGE_BUTTON_COPY}
            </button>
          ) : null}
        </div>
      ) : state.kind === 'result' &&
        state.result.outcome === 'person_confirmation_required' ? (
        <PersonConfirmation
          candidates={state.result.candidates ?? []}
          onPick={onPickCandidate}
          onDifferentPerson={onDifferentPerson}
        />
      ) : state.kind === 'result' &&
        state.result.outcome === 'different_person_confirmed' ? (
        <div data-testid="convert-different-person-confirmed">
          <p className="lead360-muted">{DIFFERENT_PERSON_CONFIRMED_COPY}</p>
          {bridgeEnabled ? (
            <button
              type="button"
              className="ac-btn ac-btn--primary ac-btn--sm"
              data-testid="bridge-open"
              onClick={onOpenBridge}
            >
              {BRIDGE_BUTTON_COPY}
            </button>
          ) : null}
        </div>
      ) : state.kind === 'bridge-form' || state.kind === 'bridge-busy' ? (
        <div className="lead360-convert__bridge" data-testid="bridge-form">
          <p className="lead360-muted" data-testid="bridge-preview">
            Will copy from this lead:{' '}
            <strong>{leadHasName ? leadName : 'name (type below)'}</strong>
            {'. '}
            {BRIDGE_PREVIEW_PHONE_COPY}
          </p>
          {!leadHasName ? (
            <label className="lead360-convert__field">
              Patient name
              <input
                value={form.fullName}
                onChange={(e) => onFormChange({ ...form, fullName: e.target.value })}
                data-testid="bridge-fullname"
              />
            </label>
          ) : null}
          <label className="lead360-convert__field">
            Gender
            <select
              value={form.gender}
              onChange={(e) => onFormChange({ ...form, gender: e.target.value })}
              data-testid="bridge-gender"
            >
              <option value="">-</option>
              {GENDER_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </label>
          <label className="lead360-convert__field">
            Age (years)
            <input
              value={form.ageYears}
              onChange={(e) => onFormChange({ ...form, ageYears: e.target.value })}
              inputMode="numeric"
              data-testid="bridge-age"
            />
          </label>
          {(() => {
            const a = parsedAge(form.ageYears);
            return a !== null && a < MINOR_AGE_YEARS;
          })() ? (
            <div data-testid="bridge-minor-fields">
              <p className="lead360-muted">
                Under 18: a guardian and recorded consent are required.
              </p>
              <label className="lead360-convert__field">
                Guardian name
                <input
                  value={form.guardianName}
                  onChange={(e) =>
                    onFormChange({ ...form, guardianName: e.target.value })
                  }
                  data-testid="bridge-guardian-name"
                />
              </label>
              <label className="lead360-convert__field">
                Relationship
                <select
                  value={form.guardianRelationship}
                  onChange={(e) =>
                    onFormChange({ ...form, guardianRelationship: e.target.value })
                  }
                >
                  {RELATIONSHIP_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="lead360-convert__field">
                Consent granted by
                <input
                  value={form.consentGrantedBy}
                  onChange={(e) =>
                    onFormChange({ ...form, consentGrantedBy: e.target.value })
                  }
                  data-testid="bridge-consent-granted-by"
                />
              </label>
              <label className="lead360-convert__field">
                Consent channel
                <select
                  value={form.consentChannel}
                  onChange={(e) =>
                    onFormChange({ ...form, consentChannel: e.target.value })
                  }
                >
                  {CONSENT_CHANNEL_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          {formError !== null ? (
            <p className="lead360-muted" data-testid="bridge-form-error" data-tone="error">
              {formError}
            </p>
          ) : null}
          <div className="lead360-convert__bridge-actions">
            <button
              type="button"
              className="ac-btn ac-btn--primary ac-btn--sm"
              data-testid="bridge-submit"
              disabled={state.kind === 'bridge-busy'}
              onClick={onSubmitBridge}
            >
              {state.kind === 'bridge-busy' ? BRIDGE_BUSY_COPY : 'Create and link'}
            </button>
            <button
              type="button"
              className="ac-btn ac-btn--secondary ac-btn--sm"
              disabled={state.kind === 'bridge-busy'}
              onClick={onCancelBridge}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : bridgeResult !== null ? (
        bridgeResult.outcome === 'linked' ||
        bridgeResult.outcome === 'created_and_linked' ? (
          <>
            <LinkedLine
              patient={bridgeResult.linkedPatient}
              justCreated={bridgeResult.outcome === 'created_and_linked'}
            />
            <PaymentDoor
              patientId={bridgeResult.patientId}
              paymentHrefFor={paymentHrefFor}
            />
          </>
        ) : bridgeResult.outcome === 'already_linked' ? (
          <p className="lead360-muted" data-testid="convert-already-linked">
            This lead is already linked to a patient record.
          </p>
        ) : bridgeResult.outcome === 'person_confirmation_required' ? (
          <PersonConfirmation
            candidates={bridgeResult.candidates ?? []}
            onPick={onPickCandidate}
            onDifferentPerson={onDifferentPerson}
            differentPersonCreates
          />
        ) : bridgeResult.outcome === 'phone_unavailable' ? (
          <p className="lead360-muted" data-testid="bridge-phone-unavailable">
            {BRIDGE_PHONE_UNAVAILABLE_COPY}
          </p>
        ) : bridgeResult.outcome === 'no_phone_on_lead' ? (
          <p className="lead360-muted" data-testid="bridge-no-phone">
            {BRIDGE_NO_PHONE_COPY}
          </p>
        ) : (
          <div data-testid="bridge-created-not-linked">
            <p className="lead360-muted" data-tone="error">
              {BRIDGE_CREATED_NOT_LINKED_COPY}
            </p>
            <button
              type="button"
              className="ac-btn ac-btn--primary ac-btn--sm"
              data-testid="bridge-retry-link"
              onClick={onRetryLink}
            >
              Link now
            </button>
          </div>
        )
      ) : state.kind === 'error' ? (
        <p className="lead360-muted" data-testid="convert-error" data-tone="error">
          {state.message}
        </p>
      ) : initialLink !== null && initialLink.status === 'possible_match' ? (
        <p className="lead360-muted" data-testid="convert-initial-possible">
          {IDLE_POSSIBLE_MATCH_COPY}
          {typeof initialLink.candidateCount === 'number' &&
          initialLink.candidateCount > 1
            ? ` (${initialLink.candidateCount} patients share this number.)`
            : ''}
        </p>
      ) : initialLink !== null && initialLink.status === 'none' ? (
        <p className="lead360-muted" data-testid="convert-initial-none">
          {IDLE_NONE_COPY}
        </p>
      ) : (
        <p className="lead360-muted">
          Link this lead to the org-level patient golden record. Matches are found
          by the patient&apos;s phone; no clinical data is touched.
        </p>
      )}

      {/* ONE PRIMARY ACTION PER STATE: once the step is asking who this
          enquiry is for, its own person actions ARE the primary actions --
          re-offering "check who this number belongs to" underneath them
          would put two primaries on one screen and invite a press that
          re-asks the question already on show. Same once the operator has
          answered "a different person": the answer stands until they act. */}
      {isLinkedState ||
      state.kind === 'bridge-form' ||
      state.kind === 'bridge-busy' ||
      (state.kind === 'result' &&
        (state.result.outcome === 'person_confirmation_required' ||
          state.result.outcome === 'different_person_confirmed')) ||
      (bridgeResult !== null &&
        (bridgeResult.outcome === 'already_linked' ||
          bridgeResult.outcome === 'person_confirmation_required' ||
          bridgeResult.outcome === 'created_not_linked')) ? null : (
        <button
          type="button"
          className="ac-btn ac-btn--primary ac-btn--sm"
          data-testid="convert-submit"
          disabled={busy}
          onClick={onConvert}
        >
          {busy ? CONVERT_BUSY_COPY : CONVERT_BUTTON_COPY}
        </button>
      )}
    </section>
  );
}

// --- Container ---------------------------------------------------------------

// V1-P6 CORRECTIVE (founder live finding 2026-07-11, Branch B): the
// first-non-cancelled-card door pick that lived here was RETIRED -- it sent a
// payment to episode 44 while the Rs 8,888 obligation lived on the collect
// source episode. The Record-payment door now consumes THE CONTRACT
// (record/current-truth.ts paymentDoorAppointmentId); no independent episode
// selection may return here.

export interface ConvertLeadToPatientProps {
  leadId: number;
  /** UX gate for the bridge affordance (me.patientMasterEnabled + a
   *  create-audience role); backend auth is the boundary. */
  bridgeEnabled?: boolean;
  /** The lead's display name (masked-preview + name-field decision). */
  leadName?: string | null;
  /** On-load link state (record.patientLink; absent = unknown). */
  initialLink?: InitialPatientLinkView | null;
  /** Billing door builder (null = no billing-entry access for the viewer). */
  paymentHrefFor?: ((patientId: string) => string | null) | null;
  /** Injected for tests; defaults to the real client fns. */
  convert?: (
    leadId: number,
    patientChoice?: LeadPersonChoiceRequest,
  ) => Promise<ConvertLeadToPatientResultView>;
  bridge?: (request: LeadBridgeRequest) => Promise<LeadBridgeResultView>;
  /** Called after a successful link so the parent can refresh. */
  onConverted?: () => void;
}

export default function ConvertLeadToPatientControl({
  leadId,
  bridgeEnabled = false,
  leadName = null,
  initialLink = null,
  paymentHrefFor = null,
  convert = convertLeadToPatientApi,
  bridge = postLeadBridgeApi,
  onConverted,
}: ConvertLeadToPatientProps): JSX.Element {
  const [state, setState] = useState<ConvertState>({ kind: 'idle' });
  const [form, setForm] = useState<BridgeFormState>(EMPTY_BRIDGE_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const leadHasName = leadName !== null && leadName.trim() !== '';

  const runConvert = (patientChoice?: LeadPersonChoiceRequest): void => {
    setState({ kind: 'busy' });
    convert(leadId, patientChoice)
      .then((result) => {
        setState({ kind: 'result', result });
        if (result.outcome === 'linked') onConverted?.();
      })
      .catch((err: unknown) => setState({ kind: 'error', message: messageFor(err) }));
  };

  const runBridge = (request: LeadBridgeRequest): void => {
    setState({ kind: 'bridge-busy' });
    bridge(request)
      .then((result) => {
        setState({ kind: 'bridge-result', result });
        if (
          result.outcome === 'linked' ||
          result.outcome === 'created_and_linked'
        ) {
          onConverted?.();
        }
      })
      .catch((err: unknown) => setState({ kind: 'error', message: messageFor(err) }));
  };

  const submitBridge = (): void => {
    const error = validateBridgeForm(form, leadHasName);
    setFormError(error);
    if (error !== null) return;
    runBridge(buildBridgeRequest(leadId, form, leadHasName));
  };

  // The explicit person choice can arrive from EITHER flow's confirmation
  // step; each door re-validates the pick against the people IT surfaced, so
  // routing the answer back to the door that asked is what keeps that fence
  // meaningful.
  const pickCandidate = (patientId: string): void => {
    const patientChoice: LeadPersonChoiceRequest = { action: 'link', patientId };
    if (state.kind === 'result') {
      runConvert(patientChoice);
      return;
    }
    runBridge({ leadId, patientChoice });
  };

  // "This enquiry is for a different person." On the convert door the lead is
  // simply left unlinked; on the bridge door the same answer is what unlocks
  // creating a NEW record for the person who actually enquired.
  const confirmDifferentPerson = (): void => {
    const patientChoice: LeadPersonChoiceRequest = { action: 'create_new' };
    if (state.kind === 'result') {
      runConvert(patientChoice);
      return;
    }
    runBridge({ leadId, patientChoice });
  };

  return (
    <ConvertLeadToPatientView
      state={state}
      bridgeEnabled={bridgeEnabled}
      leadName={leadName}
      initialLink={initialLink}
      paymentHrefFor={paymentHrefFor}
      form={form}
      onFormChange={(next) => setForm(next)}
      onConvert={() => runConvert()}
      onPickCandidate={pickCandidate}
      onDifferentPerson={confirmDifferentPerson}
      onOpenBridge={() => {
        setFormError(null);
        setState({ kind: 'bridge-form' });
      }}
      onSubmitBridge={submitBridge}
      onCancelBridge={() => setState({ kind: 'idle' })}
      onRetryLink={() => runBridge({ leadId })}
      formError={formError}
    />
  );
}
